// assets/belforti-motion.js — Belforti signature motion. Loaded with defer after theme.js,
// so BaseElement (theme.js) and the global Motion (vendor.js) are available.
// House rules: one dominant gesture per viewport, transform/opacity only,
// nothing hidden under reduced motion, scroll effects off below 768px.

// Light sweep: a soft band of light crosses the finish as the image scrolls through
// the viewport. Wrap the media in <belforti-light-sweep>; the band is injected here,
// so without JS (or with reduced motion) the image is simply untouched.
if (!customElements.get('belforti-light-sweep')) {
  class BelfortiLightSweep extends BaseElement {
    connectedCallback() {
      super.connectedCallback();
      if (theme.config.motionReduced || window.matchMedia('(max-width: 767px)').matches || typeof Motion === 'undefined') return;

      const band = document.createElement('span');
      band.className = 'belforti-light-sweep__band';
      band.setAttribute('aria-hidden', 'true');
      this.appendChild(band);

      const stop = Motion.scroll(
        Motion.animate(band, { transform: ['translateX(-120%) skewX(-12deg)', 'translateX(220%) skewX(-12deg)'] }, { easing: 'linear' }),
        { target: this, offset: ['start end', 'end start'] }
      );
      this.registerCleanup(() => {
        stop();
        band.remove();
      });
    }
  }
  customElements.define('belforti-light-sweep', BelfortiLightSweep);
}

const belfortiIsSmall = () => window.matchMedia('(max-width: 767px)').matches;

// Runs setup() while the media query matches and its teardown when it stops matching, so a
// desktop-only effect switches off cleanly when the window narrows or a phone rotates (and back
// on when it widens), instead of leaving pinned heights and transforms on the phone layout.
const belfortiWhileMatches = (element, query, setup) => {
  const mql = window.matchMedia(query);
  let teardown = null;
  const sync = () => {
    if (mql.matches && !teardown) teardown = setup() || (() => {});
    else if (!mql.matches && teardown) { teardown(); teardown = null; }
  };
  sync();
  mql.addEventListener('change', sync);
  element.registerCleanup(() => {
    mql.removeEventListener('change', sync);
    if (teardown) teardown();
  });
};

// Manifesto: words start dim and light up one by one as the statement scrolls through
// the viewport. Without JS or with reduced motion the words are simply full strength.
if (!customElements.get('belforti-manifesto')) {
  class BelfortiManifesto extends BaseElement {
    connectedCallback() {
      super.connectedCallback();
      if (theme.config.motionReduced || typeof Motion === 'undefined') return;
      const words = Array.from(this.querySelectorAll('.w'));
      if (!words.length) return;
      const dim = 0.16;
      const paint = (progress) => {
        const lit = progress * (words.length + 3);
        words.forEach((word, i) => {
          word.style.opacity = Math.min(1, Math.max(dim, dim + (lit - i) * (1 - dim)));
        });
      };
      paint(0);
      const stop = Motion.scroll(({ y }) => paint(y.progress), { target: this, offset: ['start 0.85', 'end 0.45'] });
      this.registerCleanup(() => {
        stop();
        words.forEach((word) => word.style.removeProperty('opacity'));
      });
    }
  }
  customElements.define('belforti-manifesto', BelfortiManifesto);
}

// Model rail: on desktop, pin the rail and slide the panels sideways as the page scrolls.
// Phones, reduced motion and no-JS keep the default horizontal swipe row.
if (!customElements.get('belforti-model-rail')) {
  class BelfortiModelRail extends BaseElement {
    connectedCallback() {
      super.connectedCallback();
      if (theme.config.motionReduced || typeof Motion === 'undefined') return;
      const track = this.querySelector('[data-rail-track]');
      const panels = track ? track.children.length : 0;
      if (panels < 2) return;
      belfortiWhileMatches(this, '(min-width: 768px)', () => this.scrub(track, panels));
    }

    scrub(track, panels) {
      this.classList.add('is-scrubbed');
      const offset = ['start start', 'end end'];
      const stops = [
        Motion.scroll(
          Motion.animate(track, { transform: ['translateX(0%)', `translateX(-${((panels - 1) / panels) * 100}%)`] }, { easing: 'linear' }),
          { target: this, offset }
        )
      ];
      const bar = this.querySelector('[data-rail-progress]');
      if (bar) {
        stops.push(Motion.scroll(Motion.animate(bar, { transform: ['scaleX(0)', 'scaleX(1)'] }, { easing: 'linear' }), { target: this, offset }));
      }
      return () => {
        stops.forEach((stop) => stop());
        this.classList.remove('is-scrubbed');
        track.style.removeProperty('transform');
        if (bar) bar.style.removeProperty('transform');
      };
    }
  }
  customElements.define('belforti-model-rail', BelfortiModelRail);
}

// Process: the step crossing the middle of the viewport becomes active and swaps the
// pinned photo (desktop). Phones show each step's own photo, so nothing to do there.
if (!customElements.get('belforti-process')) {
  class BelfortiProcess extends BaseElement {
    connectedCallback() {
      super.connectedCallback();
      if (belfortiIsSmall() || typeof Motion === 'undefined') return;
      const steps = Array.from(this.querySelectorAll('.belforti-process__step'));
      const images = Array.from(this.querySelectorAll('.belforti-process__stage img'));
      if (!steps.length) return;

      this.classList.add('is-live');
      let current = -1;
      const activate = (index) => {
        if (index === current) return;
        current = index;
        steps.forEach((step, i) => step.classList.toggle('is-active', i === index));
        images.forEach((img, i) => img.classList.toggle('is-active', i === index));
      };
      // The step whose middle is nearest the middle of the screen is the one in hand. Measured on every scroll
      // frame rather than with an intersection band, so short lists on tall screens still step through.
      const headerHeight = () => parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-height')) || 0;
      const pick = () => {
        // The line the names pass: the centre of the photo itself, wherever the card currently sits.
        const sr = stage ? stage.getBoundingClientRect() : null;
        const mid = sr && sr.height ? sr.top + sr.height / 2 : (window.innerHeight + headerHeight()) / 2;
        let best = 0; let dist = Infinity;
        steps.forEach((step, i) => {
          const r = step.getBoundingClientRect();
          const d = Math.abs(r.top + r.height / 2 - mid);
          if (d < dist) { dist = d; best = i; }
        });
        activate(best);
      };
      this.registerCleanup(Motion.scroll(pick, { target: this, offset: ['start end', 'end start'] }));
      pick();
      this.registerCleanup(() => this.classList.remove('is-live'));
    }
  }
  customElements.define('belforti-process', BelfortiProcess);
}

// Hero: as the page scrolls away from the first screen, the film eases back a little and
// the text lifts and fades, so the hero hands over to the page instead of just sliding off.
if (!customElements.get('belforti-hero')) {
  class BelfortiHero extends BaseElement {
    connectedCallback() {
      super.connectedCallback();
      if (theme.config.motionReduced || typeof Motion === 'undefined') return;
      belfortiWhileMatches(this, '(min-width: 768px)', () => this.drift());
    }

    drift() {
      const media = this.querySelector('[data-hero-media]');
      const body = this.querySelector('[data-hero-body]');
      const stops = [];
      const offset = ['start start', 'end start'];
      if (media) stops.push(Motion.scroll(Motion.animate(media, { transform: ['scale(1)', 'scale(1.08)'] }, { easing: 'linear' }), { target: this, offset }));
      if (body) stops.push(Motion.scroll(Motion.animate(body, { transform: ['translateY(0)', 'translateY(-12vh)'], opacity: [1, 0] }, { easing: 'linear' }), { target: this, offset }));
      return () => {
        stops.forEach((stop) => stop());
        [media, body].forEach((el) => el && el.style.removeProperty('transform'));
        if (body) body.style.removeProperty('opacity');
      };
    }
  }
  customElements.define('belforti-hero', BelfortiHero);
}


// Showcase: the instrument stage pins on desktop and the glass cards pop in one after another
// as the page scrolls; the instrument settles from slightly small to full size. Below 1024px
// the cards pile up over the instrument with CSS sticky alone, so there is nothing to do here.
// Reduced motion and no-JS show every card.
if (!customElements.get('belforti-showcase')) {
  class BelfortiShowcase extends BaseElement {
    connectedCallback() {
      super.connectedCallback();
      if (theme.config.motionReduced || typeof Motion === 'undefined') return;
      const cards = Array.from(this.querySelectorAll('[data-showcase-card]'));
      const instrument = this.querySelector('[data-showcase-instrument]');
      if (!cards.length) return;
      belfortiWhileMatches(this, '(min-width: 1024px)', () => this.stage(cards, instrument));
    }

    stage(cards, instrument) {
      this.classList.add('is-armed');
      const offset = ['start start', 'end end'];
      const stops = [
        Motion.scroll(({ y }) => {
          // Leave a short beat before the first card and keep the last one on screen a while.
          const shown = Math.floor(y.progress * (cards.length + 1.4) + 0.2);
          cards.forEach((card, i) => card.classList.toggle('is-on', i < shown));
        }, { target: this, offset })
      ];
      if (instrument) {
        stops.push(Motion.scroll(
          Motion.animate(instrument, { transform: ['translateX(-50%) scale(0.92)', 'translateX(-50%) scale(1)'] }, { easing: 'linear' }),
          { target: this, offset: ['start end', 'start start'] }
        ));
      }
      return () => {
        stops.forEach((stop) => stop());
        this.classList.remove('is-armed');
        cards.forEach((card) => card.classList.remove('is-on'));
        if (instrument) instrument.style.removeProperty('transform');
      };
    }
  }
  customElements.define('belforti-showcase', BelfortiShowcase);
}

// Detail tour (sections/belforti-tour.liquid): one continuous camera move over the whole
// instrument, driven by scroll. For each detail the camera glides to its spot, pushes in, dives
// through into the close-up (the card pops in), holds, then pulls back out before the next
// glide. The play head eases toward the scroll position every frame, so wheel steps still read
// as one smooth move. Only transforms and opacity are written.
if (!customElements.get('belforti-tour')) {
  const clamp01 = (t) => Math.min(1, Math.max(0, t));
  const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  const lerp = (a, b, t) => a + (b - a) * t;
  const span = (t, from, to) => clamp01((t - from) / (to - from));

  class BelfortiTour extends BaseElement {
    connectedCallback() {
      super.connectedCallback();
      if (theme.config.motionReduced || typeof Motion === 'undefined') return;
      this.stage = this.querySelector('[data-tour-stage]');
      this.camera = this.querySelector('[data-tour-camera]');
      this.front = this.querySelector('[data-tour-front]');
      this.back = this.querySelector('[data-tour-back]');
      this.head = this.querySelector('[data-tour-head]');
      this.steps = Array.from(this.querySelectorAll('[data-tour-step]')).map((el) => ({
        el,
        shot: el.querySelector('[data-tour-shot]'),
        side: el.dataset.side === 'back' ? 'back' : 'front',
        x: Number(el.dataset.mx) / 100,
        y: Number(el.dataset.my) / 100,
      }));
      this.dots = Array.from(this.querySelectorAll('[data-tour-dot]'));
      if (!this.steps.length || !this.camera) return;

      // The film, in scroll units: a beat on the whole instrument, then per detail a glide,
      // a dive, a hold and a rise, then back out to the whole instrument. The hold is the longest
      // part, and the scroll comes to rest on it (see settle), so each feature is a clear stop.
      this.segments = [{ kind: 'intro', len: 0.4 }];
      this.steps.forEach((_, k) => {
        this.segments.push(
          { kind: 'glide', k, len: 0.8 },
          { kind: 'dive', k, len: 0.5 },
          { kind: 'hold', k, len: 1.2 },
          { kind: 'rise', k, len: 0.4 }
        );
      });
      this.segments.push({ kind: 'outro', len: 0.7 }, { kind: 'end', len: 0.2 });
      this.total = this.segments.reduce((sum, s) => sum + s.len, 0);
      // Resting points: the opening view, the middle of each hold, the closing view.
      this.stops = [0];
      this.segments.reduce((at, s) => {
        if (s.kind === 'hold') this.stops.push(at + s.len * 0.45);
        return at + s.len;
      }, 0);
      this.stops.push(this.total);

      this.classList.add('is-armed');
      this.style.height = `calc(100svh + ${(this.total * 18).toFixed(1)}svh)`;
      this.measure();
      this.current = 0;
      this.target = 0;
      this.frame = null;
      this.render(0);

      const stop = Motion.scroll(({ y }) => {
        this.target = y.progress * this.total;
        this.tick();
        this.direction = Math.sign(this.target - (this.last ?? this.target)) || this.direction || 1;
        this.last = this.target;
        clearTimeout(this.settleTimer);
        if (!this.touching && !this.gliding) this.settleTimer = setTimeout(() => this.settle(), 160);
      }, { target: this, offset: ['start start', 'end end'] });
      const onTouchStart = () => {
        this.touching = true;
        clearTimeout(this.settleTimer);
        this.stopGlide();
      };
      // Any new input takes the scroll back from a glide in progress.
      const onTakeOver = () => this.stopGlide();
      const onTouchEnd = () => {
        this.touching = false;
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.settle(), 220);
      };
      window.addEventListener('touchstart', onTouchStart, { passive: true });
      window.addEventListener('touchend', onTouchEnd, { passive: true });
      window.addEventListener('wheel', onTakeOver, { passive: true });
      window.addEventListener('keydown', onTakeOver);
      const onResize = () => {
        this.measure();
        this.render(this.current);
      };
      window.addEventListener('resize', onResize);

      this.registerCleanup(() => {
        stop();
        cancelAnimationFrame(this.frame);
        window.removeEventListener('resize', onResize);
        window.removeEventListener('touchstart', onTouchStart);
        window.removeEventListener('touchend', onTouchEnd);
        window.removeEventListener('wheel', onTakeOver);
        window.removeEventListener('keydown', onTakeOver);
        this.stopGlide();
        clearTimeout(this.settleTimer);
        this.classList.remove('is-armed');
        this.style.removeProperty('height');
        [this.camera, this.head, this.front, this.back].forEach((el) => el && el.removeAttribute('style'));
        this.steps.forEach((s) => {
          s.el.classList.remove('is-on');
          s.shot.removeAttribute('style');
        });
      });
    }

    // When the scroll comes to rest inside the film, carry it on to the next resting point in the
    // direction of travel, unless it barely left the last one. A small scroll always lands on a
    // whole feature, never between two.
    settle() {
      const u = this.target;
      if (u <= 0.001 || u >= this.total - 0.001) return;
      const next = this.stops.findIndex((s) => s >= u);
      const after = this.stops[next];
      const before = this.stops[next - 1];
      if (before === undefined) return;
      const frac = (u - before) / (after - before);
      const goal = this.direction > 0 ? (frac > 0.12 ? after : before) : (frac < 0.88 ? before : after);
      const range = this.offsetHeight - window.innerHeight;
      const top = this.getBoundingClientRect().top + window.scrollY + (goal / this.total) * range;
      if (Math.abs(top - window.scrollY) > 2) this.glideTo(top);
    }

    // A slow, eased scroll to the resting point (the browser's own smooth scroll is too quick
    // for a camera move). Longer distances take a little longer, within 1.1 to 2 seconds.
    glideTo(top) {
      this.stopGlide();
      const from = window.scrollY;
      const distance = top - from;
      const duration = Math.min(2000, Math.max(1100, 900 + Math.abs(distance) * 0.5));
      const start = performance.now();
      this.gliding = true;
      const step = (now) => {
        const t = clamp01((now - start) / duration);
        window.scrollTo({ top: from + distance * ease(t), behavior: 'instant' });
        this.glideFrame = t < 1 ? requestAnimationFrame(step) : null;
        if (t === 1) this.gliding = false;
      };
      this.glideFrame = requestAnimationFrame(step);
    }

    stopGlide() {
      cancelAnimationFrame(this.glideFrame);
      this.glideFrame = null;
      this.gliding = false;
    }

    // Ease the play head toward the scroll position, one frame at a time.
    tick() {
      if (this.frame) return;
      const step = () => {
        const diff = this.target - this.current;
        this.current = Math.abs(diff) < 0.0005 ? this.target : this.current + diff * 0.09;
        this.render(this.current);
        this.frame = this.current === this.target ? null : requestAnimationFrame(step);
      };
      this.frame = requestAnimationFrame(step);
    }

    // The instrument fits the stage below the header; the camera aims at the centre of that area.
    measure() {
      const stageW = this.stage.clientWidth;
      const stageH = this.stage.clientHeight;
      const top = parseFloat(getComputedStyle(this.stage).getPropertyValue('--header-height')) || 0;
      const img = this.front.querySelector('img');
      const ratio = img && img.naturalWidth ? img.naturalWidth / img.naturalHeight : 0.8;
      const availH = stageH - top;
      const h = Math.min(availH, (stageW * 0.92) / ratio);
      this.box = { w: h * ratio, h, cx: stageW / 2, cy: top + availH / 2 };
      // Close-ups need more zoom when the instrument is small on screen (phones).
      const small = Math.max(1, availH / h);
      this.near = 3.2 * small;
      this.mid = 1.9 * small;
      this.camera.style.width = `${this.box.w}px`;
      this.camera.style.height = `${this.box.h}px`;
      if (img && !img.complete) {
        img.addEventListener('load', () => {
          this.measure();
          this.render(this.current);
        }, { once: true });
      }
    }

    pose(kind, k) {
      if (kind === 'full') return { s: 1, x: 0.5, y: 0.5 };
      const step = this.steps[k];
      return { s: kind === 'near' ? this.near : this.mid, x: step.x, y: step.y };
    }

    // Zoom moves on a log scale so pushing in feels even; position follows the same curve.
    mix(a, b, t) {
      const e = ease(t);
      return { s: Math.exp(lerp(Math.log(a.s), Math.log(b.s), e)), x: lerp(a.x, b.x, e), y: lerp(a.y, b.y, e) };
    }

    render(u) {
      let at = 0;
      let seg = this.segments[this.segments.length - 1];
      let t = 1;
      for (const s of this.segments) {
        if (u < at + s.len) {
          seg = s;
          t = (u - at) / s.len;
          break;
        }
        at += s.len;
      }
      const last = this.steps.length - 1;
      const sideOf = (k) => (this.steps[k].side === 'back' ? 1 : 0);
      let cam = this.pose('full');
      let backAmount = sideOf(0);
      let shot = null;
      let on = -1;
      let current = -1;
      let head = 0;

      switch (seg.kind) {
        case 'intro':
          head = 1;
          break;
        case 'glide': {
          const k = seg.k;
          current = k;
          const to = this.pose('mid', k);
          const from = k === 0 ? this.pose('full') : this.pose('mid', k - 1);
          const prev = k === 0 ? sideOf(0) : sideOf(k - 1);
          const next = sideOf(k);
          if (prev !== next) {
            // Turn the instrument round on the full view, halfway through the glide.
            cam = t < 0.5 ? this.mix(from, this.pose('full'), t * 2) : this.mix(this.pose('full'), to, (t - 0.5) * 2);
            backAmount = lerp(prev, next, span(t, 0.38, 0.62));
          } else {
            cam = this.mix(from, to, t);
            backAmount = next;
          }
          if (k === 0) head = 1 - span(t, 0, 0.45);
          break;
        }
        case 'dive': {
          const k = seg.k;
          const reveal = ease(span(t, 0.25, 1));
          current = k;
          cam = this.mix(this.pose('mid', k), this.pose('near', k), t);
          backAmount = sideOf(k);
          shot = { k, o: reveal, s: lerp(1.35, 1, reveal) };
          break;
        }
        case 'hold': {
          const k = seg.k;
          current = k;
          cam = this.pose('near', k);
          backAmount = sideOf(k);
          shot = { k, o: 1, s: 1 + 0.06 * t };
          if (t > 0.06 && t < 0.86) on = k;
          break;
        }
        case 'rise': {
          const k = seg.k;
          current = k;
          cam = this.mix(this.pose('near', k), this.pose('mid', k), t);
          backAmount = sideOf(k);
          shot = { k, o: 1 - ease(span(t, 0, 0.8)), s: lerp(1.06, 0.82, ease(t)) };
          break;
        }
        case 'outro':
          cam = this.mix(this.pose('mid', last), this.pose('full'), t);
          backAmount = sideOf(last);
          head = span(t, 0.6, 1);
          break;
        default:
          backAmount = sideOf(last);
          head = 1;
      }

      const { w, h, cx, cy } = this.box;
      this.camera.style.transform = `translate3d(${(cx - cam.s * cam.x * w).toFixed(2)}px, ${(cy - cam.s * cam.y * h).toFixed(2)}px, 0) scale(${cam.s.toFixed(4)})`;
      // The turn reads as the instrument spinning on its axis: one side narrows to an edge, the
      // other opens from it. The two photos are framed differently, so they never overlap.
      const spin = Math.cos(backAmount * Math.PI);
      this.front.style.opacity = backAmount < 0.5 ? '1' : '0';
      this.back.style.opacity = backAmount < 0.5 ? '0' : '1';
      this.front.style.transform = backAmount > 0 && backAmount < 0.5 ? `scaleX(${spin.toFixed(4)})` : '';
      this.back.style.transform = backAmount > 0.5 && backAmount < 1 ? `scaleX(${(-spin).toFixed(4)})` : '';
      this.head.style.opacity = head.toFixed(3);
      this.head.style.pointerEvents = head > 0.5 ? '' : 'none';
      this.head.style.transform = `translateY(${((1 - head) * -16).toFixed(1)}px)`;

      this.steps.forEach((step, k) => {
        const visible = shot && shot.k === k && shot.o > 0.001;
        step.shot.style.visibility = visible ? 'visible' : 'hidden';
        if (visible) {
          step.shot.style.opacity = shot.o.toFixed(3);
          step.shot.style.transform = `scale(${shot.s.toFixed(4)})`;
        }
        step.el.classList.toggle('is-on', k === on);
      });
      this.dots.forEach((dot, k) => dot.classList.toggle('is-current', k === current));
    }
  }
  customElements.define('belforti-tour', BelfortiTour);
}

// Closing scene (sections/belforti-close.liquid): the photo opens from a small window to the
// full screen while the two halves of the headline, set just outside the window, close in to
// meet over it; then the line and the actions settle in. Scrubbed by scroll, transforms,
// clip-path and opacity only.
if (!customElements.get('belforti-close')) {
  const closeClamp = (t) => Math.min(1, Math.max(0, t));
  const closeSpan = (t, from, to) => closeClamp((t - from) / (to - from));
  const closeEase = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  class BelfortiClose extends BaseElement {
    connectedCallback() {
      super.connectedCallback();
      if (theme.config.motionReduced || typeof Motion === 'undefined') return;
      this.stage = this.querySelector('[data-close-stage]');
      this.media = this.querySelector('[data-close-media]');
      this.img = this.media && this.media.querySelector('img');
      this.top = this.querySelector('[data-close-top]');
      this.bottom = this.querySelector('[data-close-bottom]');
      this.end = this.querySelector('[data-close-end]');
      if (!this.stage || !this.media) return;

      this.classList.add('is-armed');
      this.measure();
      this.render(0);
      const stop = Motion.scroll(({ y }) => this.render(y.progress), { target: this, offset: ['start start', 'end end'] });
      const onResize = () => {
        this.measure();
        this.render(this.progress || 0);
      };
      window.addEventListener('resize', onResize);
      this.registerCleanup(() => {
        stop();
        window.removeEventListener('resize', onResize);
        this.classList.remove('is-armed');
        [this.media, this.img, this.top, this.bottom, this.end].forEach((el) => el && el.removeAttribute('style'));
      });
    }

    // Where the window starts, and how far each half of the headline sits from its resting place.
    measure() {
      const small = window.innerWidth < 768;
      this.inset = { y: small ? 30 : 27, x: small ? 12 : 35 };
      [this.top, this.bottom].forEach((el) => el && (el.style.transform = ''));
      const stage = this.stage.getBoundingClientRect();
      const gap = Math.max(12, stage.height * 0.02);
      const centre = (el) => {
        const r = el.getBoundingClientRect();
        return { y: r.top - stage.top + r.height / 2, h: r.height };
      };
      if (this.top) {
        const c = centre(this.top);
        this.topShift = stage.height * (this.inset.y / 100) - gap - c.h / 2 - c.y;
      }
      if (this.bottom) {
        const c = centre(this.bottom);
        this.bottomShift = stage.height * (1 - this.inset.y / 100) + gap + c.h / 2 - c.y;
      }
    }

    render(p) {
      this.progress = p;
      const open = closeEase(closeSpan(p, 0.04, 0.6));
      const y = (this.inset.y * (1 - open)).toFixed(2);
      const x = (this.inset.x * (1 - open)).toFixed(2);
      const r = (24 * (1 - open)).toFixed(1);
      this.media.style.clipPath = `inset(${y}% ${x}% ${y}% ${x}% round ${r}px)`;
      this.media.style.setProperty('--bc-shade', (0.12 + 0.43 * open).toFixed(3));
      if (this.img) this.img.style.transform = `scale(${(1.3 - 0.3 * open).toFixed(4)})`;
      if (this.top) this.top.style.transform = `translateY(${(this.topShift * (1 - open)).toFixed(1)}px)`;
      if (this.bottom) this.bottom.style.transform = `translateY(${(this.bottomShift * (1 - open)).toFixed(1)}px)`;
      if (this.end) {
        const e = closeEase(closeSpan(p, 0.6, 0.82));
        this.end.style.opacity = e.toFixed(3);
        this.end.style.transform = `translateY(${(24 * (1 - e)).toFixed(1)}px)`;
        this.end.style.pointerEvents = e > 0.5 ? '' : 'none';
      }
    }
  }
  customElements.define('belforti-close', BelfortiClose);
}

// Feature ledger (sections/belforti-ledger.liquid): one feature open at a time, its photo on
// the pinned stage; hovering a name previews that photo, clicking opens the feature. Without
// JS every feature stays open (the folded state only applies under .is-live).
if (!customElements.get('belforti-ledger')) {
  class BelfortiLedger extends BaseElement {
    connectedCallback() {
      super.connectedCallback();
      const items = Array.from(this.querySelectorAll('[data-ledger-item]'));
      const toggles = Array.from(this.querySelectorAll('[data-ledger-toggle]'));
      const images = Array.from(this.querySelectorAll('.bld__stage img'));
      const caption = this.querySelector('[data-ledger-caption]');
      const list = this.querySelector('[data-ledger-list]');
      if (items.length < 2) return;

      let current = Math.max(0, items.findIndex((item) => item.classList.contains('is-open')));
      const show = (index) => {
        images.forEach((img, i) => img.classList.toggle('is-active', i === index));
        if (caption && toggles[index]) caption.textContent = toggles[index].dataset.name || '';
      };
      const open = (index) => {
        current = index;
        items.forEach((item, i) => item.classList.toggle('is-open', i === index));
        toggles.forEach((toggle, i) => toggle.setAttribute('aria-expanded', String(i === index)));
        show(index);
      };

      this.classList.add('is-live');
      open(current);

      // Scroll-driven mode (data-drive, from 1024px): the section is taller than the screen and its grid pins;
      // the page's progress through it opens the features one after the other. A click scrolls to that stretch.
      const drive = this.hasAttribute('data-drive') && !theme.config.motionReduced && typeof Motion !== 'undefined';
      const wide = window.matchMedia('(min-width: 1024px)');
      let stop = null;
      const arm = () => {
        if (!drive || !wide.matches || stop) return;
        stop = Motion.scroll((info) => open(Math.min(items.length - 1, Math.floor(info.y.progress * items.length))), { target: this, offset: ['start start', 'end end'] });
      };
      const disarm = () => { if (stop) { stop(); stop = null; } };
      arm();
      if (drive) this.on(wide, 'change', () => (wide.matches ? arm() : disarm()));
      this.registerCleanup(disarm);

      const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      toggles.forEach((toggle, i) => {
        this.on(toggle, 'click', () => {
          if (!stop) return open(i);
          const span = this.offsetHeight - window.innerHeight;
          const top = window.scrollY + this.getBoundingClientRect().top + ((i + 0.5) / items.length) * span;
          window.scrollTo({ top, behavior: 'smooth' });
        });
        // No hover preview while the scroll drives the list: the rows move under a still pointer and would
        // keep swapping the photo away from the open feature.
        if (fine) this.on(toggle, 'pointerenter', () => { if (!stop) show(i); });
      });
      if (fine && list) this.on(list, 'pointerleave', () => show(current));
      this.registerCleanup(() => this.classList.remove('is-live'));
    }
  }
  customElements.define('belforti-ledger', BelfortiLedger);
}

// Feature reel (sections/belforti-reel.liquid): giant outlined names; the one nearest the middle
// of the screen fills in, and the pinned card beside the list shows its photo and line. Clicking
// a name scrolls it to the middle. Without JS or with reduced motion every name stays filled.
if (!customElements.get('belforti-reel')) {
  class BelfortiReel extends BaseElement {
    connectedCallback() {
      super.connectedCallback();
      if (theme.config.motionReduced || typeof Motion === 'undefined') return;
      const items = Array.from(this.querySelectorAll('[data-reel-item]'));
      const stage = this.querySelector('.brl__stage');
      const images = Array.from(this.querySelectorAll('.brl__stage img'));
      const cap = this.querySelector('[data-reel-cap]');
      const capText = this.querySelector('[data-reel-cap-text]');
      const capLink = this.querySelector('[data-reel-cap-link]');
      if (items.length < 2) return;

      this.classList.add('is-live');
      let current = -1;
      let swapTimer = 0;
      const activate = (index) => {
        if (index === current) return;
        current = index;
        items.forEach((item, i) => item.classList.toggle('is-on', i === index));
        images.forEach((img, i) => img.classList.toggle('is-active', i === index));
        if (!cap) return;
        const line = items[index].querySelector('[data-reel-line]');
        const link = items[index].querySelector('[data-reel-link]');
        cap.classList.add('is-swapping');
        clearTimeout(swapTimer);
        swapTimer = setTimeout(() => {
          if (capText) capText.textContent = line ? line.textContent : '';
          if (capLink) { capLink.textContent = link ? link.textContent : ''; capLink.href = link ? link.href : '#'; }
          cap.classList.remove('is-swapping');
        }, 220);
      };
      const pick = () => {
        const mid = window.innerHeight / 2;
        let best = 0; let dist = Infinity;
        items.forEach((item, i) => {
          const r = item.querySelector('[data-reel-name]').getBoundingClientRect();
          const d = Math.abs(r.top + r.height / 2 - mid);
          if (d < dist) { dist = d; best = i; }
        });
        activate(best);
      };
      // Below 1024px the folds open under the names, so a scroll-driven pick would move the
      // layout it measures: there the list is a tap-driven accordion, one fold open at a time.
      const small = window.matchMedia('(max-width: 1023px)');
      this.registerCleanup(Motion.scroll(() => { if (!small.matches) pick(); }, { target: this, offset: ['start end', 'end start'] }));
      if (small.matches) activate(0); else pick();
      this.on(small, 'change', () => { if (small.matches) activate(current < 0 ? 0 : current); else pick(); });
      items.forEach((item, i) => {
        const name = item.querySelector('[data-reel-name]');
        name.setAttribute('tabindex', '0');
        const open = () => {
          if (!small.matches) { item.scrollIntoView({ block: 'center', behavior: 'smooth' }); return; }
          // The fold open above this one collapses as we scroll, so its height is taken off the target.
          const openAbove = current >= 0 && current < i ? items[current].querySelector('.brl__fold-in') : null;
          const shift = openAbove ? openAbove.getBoundingClientRect().height : 0;
          activate(i);
          const top = name.getBoundingClientRect().top + window.scrollY - headerHeight() - 12 - shift;
          window.scrollTo({ top, behavior: 'smooth' });
        };
        this.on(name, 'click', open);
        this.on(name, 'keydown', (event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); open(); } });
      });
      this.registerCleanup(() => { clearTimeout(swapTimer); this.classList.remove('is-live'); });
    }
  }
  customElements.define('belforti-reel', BelfortiReel);
}

// Chapter pill (sections/belforti-chapters.liquid): shows once the hero has scrolled away,
// hides again when the closing scene arrives, and slides the light pill behind the chapter
// whose section last crossed the upper half of the viewport.
if (!customElements.get('belforti-chapters')) {
  class BelfortiChapters extends BaseElement {
    connectedCallback() {
      super.connectedCallback();
      const links = Array.from(this.querySelectorAll('[data-chapter-link]'));
      const targets = links.map((link) => document.getElementById(decodeURIComponent(link.hash.slice(1))));
      const ind = this.querySelector('[data-chapter-ind]');
      const list = this.querySelector('.bch__list');
      const closing = document.querySelector('belforti-close');
      if (!links.length) return;
      this.classList.add('is-live');

      let current = -1;
      const place = (index) => {
        if (!ind) return;
        if (index < 0) { ind.style.opacity = '0'; return; }
        const link = links[index];
        ind.style.opacity = '1';
        ind.style.width = link.offsetWidth + 'px';
        ind.style.transform = 'translateX(' + link.parentElement.offsetLeft + 'px)';
        if (list && list.scrollWidth > list.clientWidth) {
          link.scrollIntoView({ block: 'nearest', inline: 'center', behavior: theme.config.motionReduced ? 'auto' : 'smooth' });
        }
      };
      const update = () => {
        const line = window.innerHeight * 0.45;
        let index = -1;
        targets.forEach((target, i) => { if (target && target.getBoundingClientRect().top <= line) index = i; });
        if (index !== current) {
          current = index;
          links.forEach((link, i) => link.classList.toggle('is-current', i === index));
          place(index);
        }
        const pastHero = window.scrollY > window.innerHeight * 0.6;
        const beforeClose = !closing || closing.getBoundingClientRect().top > window.innerHeight * 0.7;
        this.classList.toggle('is-shown', pastHero && beforeClose);
      };
      let ticking = false;
      const onScroll = () => { if (ticking) return; ticking = true; requestAnimationFrame(() => { ticking = false; update(); }); };
      this.on(window, 'scroll', onScroll, { passive: true });
      this.on(window, 'resize', onScroll);
      update();
      this.registerCleanup(() => { this.classList.remove('is-live', 'is-shown'); });
    }
  }
  customElements.define('belforti-chapters', BelfortiChapters);
}

// Spotlight (sections/belforti-spotlight.liquid): the headline lines rise out of their masks,
// the rule draws, the copy settles in, once, as the section comes into view; the photo eases
// from slightly large to true size while the section scrolls through (desktop). Reduced motion
// and no-JS: the section is simply at rest.
if (!customElements.get('belforti-spotlight')) {
  class BelfortiSpotlight extends BaseElement {
    connectedCallback() {
      super.connectedCallback();
      if (theme.config.motionReduced || typeof Motion === 'undefined') return;
      const lines = Array.from(this.querySelectorAll('[data-spot-line]'));
      const rule = this.querySelector('[data-spot-rule]');
      const copy = this.querySelector('[data-spot-copy]');
      const img = this.querySelector('.bsp__media img');
      const ease = [0.16, 1, 0.3, 1];
      this.classList.add('is-armed');
      this.registerCleanup(Motion.inView(this, () => {
        Motion.animate(lines, { transform: ['translateY(110%)', 'translateY(0%)'] }, { duration: 1.5, delay: Motion.stagger(0.14), easing: ease });
        if (rule) Motion.animate(rule, { transform: ['scaleX(0)', 'scaleX(1)'] }, { duration: 1.4, delay: 0.35, easing: ease });
        if (copy) Motion.animate(copy, { opacity: [0, 1], transform: ['translateY(1.25rem)', 'translateY(0)'] }, { duration: 1.2, delay: 0.55, easing: ease });
      }, { amount: 0.35 }));
      if (img) {
        belfortiWhileMatches(this, '(min-width: 768px)', () => {
          const stop = Motion.scroll(
            Motion.animate(img, { transform: ['scale(1.18) translateY(-3%)', 'scale(1) translateY(3%)'] }, { easing: 'linear' }),
            { target: this, offset: ['start end', 'end start'] }
          );
          return () => { stop(); img.style.removeProperty('transform'); };
        });
      }
      this.registerCleanup(() => this.classList.remove('is-armed'));
    }
  }
  customElements.define('belforti-spotlight', BelfortiSpotlight);
}

// Shot (sections/belforti-shot.liquid) with "opens as you scroll": each photo begins as a
// narrower, rounded window holding a slightly enlarged picture and opens to its full frame as
// it rises through the lower half of the viewport; two photos also slide in from opposite
// sides. Scrubbed by scroll, desktop only; clip-path and transforms only.
if (!customElements.get('belforti-shot')) {
  class BelfortiShot extends BaseElement {
    connectedCallback() {
      super.connectedCallback();
      if (!this.hasAttribute('data-reveal') || theme.config.motionReduced || typeof Motion === 'undefined') return;
      const frames = Array.from(this.querySelectorAll('.bsh__media'));
      if (!frames.length) return;
      belfortiWhileMatches(this, '(min-width: 768px)', () => this.open(frames));
    }

    open(frames) {
      this.classList.add('is-armed');
      const ease = (t) => 1 - Math.pow(1 - t, 3);
      const two = frames.length > 1;
      const radius = getComputedStyle(frames[0]).borderRadius || '0px';
      const paint = (progress) => {
        const p = ease(Math.min(1, Math.max(0, progress)));
        frames.forEach((frame, i) => {
          const img = frame.querySelector('img');
          const inset = (1 - p) * (two ? 6 : 10);
          const r = (1 - p) * 2 + (radius === '0px' ? 0 : 1.5);
          frame.style.clipPath = 'inset(0 ' + inset + '% round ' + r + 'rem)';
          if (two) frame.style.transform = 'translateX(' + ((i === 0 ? -1 : 1) * (1 - p) * 10) + '%)';
          if (img) img.style.transform = 'scale(' + (1 + (1 - p) * 0.18) + ')';
        });
      };
      paint(0);
      const stop = Motion.scroll(({ y }) => paint(y.progress), { target: this, offset: ['start end', 'start 0.35'] });
      return () => {
        stop();
        this.classList.remove('is-armed');
        frames.forEach((frame) => {
          frame.style.removeProperty('clip-path');
          frame.style.removeProperty('transform');
          const img = frame.querySelector('img');
          if (img) img.style.removeProperty('transform');
        });
      };
    }
  }
  customElements.define('belforti-shot', BelfortiShot);
}

// Scale (sections/belforti-scale.liquid): the stage pins while a large numeral climbs the scale
// with the scroll. The scroll is cut into one equal stretch per band; inside each stretch the
// number eases from the band's first score to its last (with a short hold at both ends), and the
// band's name, photo and text come forward as it is entered. The line at the foot fills to the
// score. Reduced motion and no-JS fall back to the static grid in the section.
if (!customElements.get('belforti-scale')) {
  const scClamp = (t) => Math.min(1, Math.max(0, t));
  const scEase = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  class BelfortiScale extends BaseElement {
    connectedCallback() {
      super.connectedCallback();
      if (theme.config.motionReduced || typeof Motion === 'undefined') return;
      const num = this.querySelector('[data-scale-num]');
      const band = this.querySelector('[data-scale-band]');
      const fill = this.querySelector('[data-scale-fill]');
      const dot = this.querySelector('[data-scale-dot]');
      const bar = this.querySelector('[data-scale-bar]');
      const imgs = Array.from(this.querySelectorAll('.bsc__img'));
      const texts = Array.from(this.querySelectorAll('[data-scale-text]'));
      const marks = Array.from(this.querySelectorAll('[data-scale-mark]'));
      const stages = texts.map((el) => ({ from: Number(el.dataset.from) || 0, name: el.dataset.name || '' }));
      const max = Number(this.dataset.max) || 100;
      const min = Number(this.dataset.min) || 0;
      const span = Math.max(1, max - min);
      if (!num || stages.length < 2) return;

      this.classList.add('is-armed');
      let width = bar ? bar.clientWidth : 0;
      let seg = -1;
      let last = 0;
      const render = (p) => {
        last = p;
        const n = stages.length;
        const k = Math.min(n - 1, Math.floor(p * n));
        const t = p * n - k;
        const e = scEase(scClamp((t - 0.12) / 0.76));
        const from = stages[k].from;
        const to = k < n - 1 ? stages[k + 1].from - 1 : max;
        const score = from + (to - from) * e;
        const f = Math.min(1, Math.max(0, (score - min) / span));
        num.textContent = String(Math.round(score));
        if (fill) fill.style.transform = 'scaleX(' + f.toFixed(4) + ')';
        if (dot) dot.style.transform = 'translateX(' + (f * width).toFixed(1) + 'px)';
        if (k !== seg) {
          seg = k;
          this.dataset.stage = String(k);
          if (band) band.textContent = stages[k].name;
          imgs.forEach((img, i) => img.classList.toggle('is-active', i === k));
          texts.forEach((el, i) => el.classList.toggle('is-active', i === k));
          marks.forEach((mark, i) => mark.classList.toggle('is-on', k >= i + 1));
        }
      };
      render(0);
      const stop = Motion.scroll(({ y }) => render(y.progress), { target: this, offset: ['start start', 'end end'] });
      const onResize = () => {
        width = bar ? bar.clientWidth : 0;
        render(last);
      };
      window.addEventListener('resize', onResize);
      this.registerCleanup(() => {
        stop();
        window.removeEventListener('resize', onResize);
        this.classList.remove('is-armed');
        this.dataset.stage = '0';
        [fill, dot].forEach((el) => el && el.removeAttribute('style'));
        imgs.forEach((img) => img.classList.remove('is-active'));
        texts.forEach((el) => el.classList.remove('is-active'));
        marks.forEach((mark) => mark.classList.remove('is-on'));
      });
    }
  }
  customElements.define('belforti-scale', BelfortiScale);
}

// Index (sections/belforti-library.liquid): under 1024px an accordion, one row open; from 1024px
// a catalogue, where the row crossing the middle of the viewport fills the pinned panel, hovering
// a row previews it and clicking scrolls to it. Without JS every description is visible.
if (!customElements.get('belforti-index')) {
  class BelfortiIndex extends BaseElement {
    connectedCallback() {
      super.connectedCallback();
      this.items = Array.from(this.querySelectorAll('[data-index-item]'));
      this.toggles = Array.from(this.querySelectorAll('[data-index-toggle]'));
      if (this.items.length < 2) return;
      this.spy = false;
      this.current = Math.max(0, this.items.findIndex((item) => item.classList.contains('is-open')));
      this.classList.add('is-live');
      this.open(this.current);
      this.toggles.forEach((toggle, i) => this.on(toggle, 'click', () => (this.spy ? this.jump(i) : this.open(i))));
      belfortiWhileMatches(this, '(min-width: 1024px)', () => this.watch());
      this.registerCleanup(() => this.classList.remove('is-live'));
    }

    open(index) {
      this.current = index;
      this.items.forEach((item, i) => item.classList.toggle('is-open', i === index));
      this.toggles.forEach((toggle, i) => toggle.setAttribute('aria-expanded', String(i === index)));
    }

    jump(index) {
      const r = this.items[index].getBoundingClientRect();
      window.scrollTo({ top: window.scrollY + r.top + r.height / 2 - window.innerHeight * 0.5, behavior: theme.config.motionReduced ? 'auto' : 'smooth' });
    }

    watch() {
      const panel = this.querySelector('[data-index-panel]');
      if (!panel) return;
      const field = (name) => panel.querySelector('[data-index-' + name + ']');
      const fields = { part: field('part'), num: field('num'), name: field('name'), text: field('text') };
      const list = this.querySelector('[data-index-list]');
      this.spy = true;
      this.classList.add('is-spy');

      let shown = -1;
      let token = 0;
      let hovering = -1;
      let ticking = false;
      const fill = (i) => {
        const item = this.items[i];
        const copy = item.querySelector('[data-index-copy]');
        if (fields.part) fields.part.textContent = item.dataset.part || '';
        if (fields.num) fields.num.textContent = item.dataset.count || '';
        if (fields.name) fields.name.textContent = item.dataset.name || '';
        if (fields.text) fields.text.innerHTML = copy ? copy.innerHTML : '';
      };
      const show = (i, instant) => {
        if (i === shown) return;
        shown = i;
        this.items.forEach((item, k) => item.classList.toggle('is-current', k === i));
        if (instant || theme.config.motionReduced) { fill(i); return; }
        const mine = ++token;
        panel.classList.add('is-out');
        setTimeout(() => {
          if (mine !== token) return;
          fill(i);
          panel.classList.remove('is-out');
        }, 170);
      };
      // The row nearest the middle of the viewport is the current one; off screen, nothing is measured.
      const update = () => {
        ticking = false;
        if (hovering >= 0) return;
        const box = this.getBoundingClientRect();
        if (box.bottom < 0 || box.top > window.innerHeight) return;
        const line = window.innerHeight * 0.5;
        let best = -1;
        let bestD = Infinity;
        this.items.forEach((item, i) => {
          const r = item.getBoundingClientRect();
          const d = r.top <= line && r.bottom >= line ? 0 : Math.min(Math.abs(r.top - line), Math.abs(r.bottom - line));
          if (d < bestD) { bestD = d; best = i; }
        });
        if (best >= 0) { this.current = best; show(best); }
      };
      const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);

      const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      const hovers = [];
      const leave = () => { hovering = -1; show(this.current); };
      if (fine) {
        this.toggles.forEach((toggle, i) => {
          const enter = () => { hovering = i; show(i); };
          toggle.addEventListener('pointerenter', enter);
          hovers.push([toggle, enter]);
        });
        if (list) list.addEventListener('pointerleave', leave);
      }
      show(this.current, true);
      update();

      return () => {
        this.spy = false;
        token++;
        this.classList.remove('is-spy');
        panel.classList.remove('is-out');
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
        hovers.forEach(([el, fn]) => el.removeEventListener('pointerenter', fn));
        if (list) list.removeEventListener('pointerleave', leave);
        this.items.forEach((item) => item.classList.remove('is-current'));
        this.open(this.current);
      };
    }
  }
  customElements.define('belforti-index', BelfortiIndex);
}

// Panels (sections/belforti-panels.liquid): the stage is cut into upright panels whose widths
// ease toward a target every frame: the panel the scroll has reached (one equal stretch of
// scroll per panel) or, while a fine pointer rests on the stage, the panel under it. The open
// panel takes --bpn-wide of the stage, the others share the rest. Each panel is a full-stage
// layer clipped to its slice; its photo is centred on the slice, its upright word rides the
// slice's left edge and fades as its body (number, word, text) comes in. With a closing photo
// the last stretch of scroll fades that in over everything. clip-path, transforms, opacity only.
if (!customElements.get('belforti-panels')) {
  const pnClamp = (t) => Math.min(1, Math.max(0, t));
  const pnEase = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  class BelfortiPanels extends BaseElement {
    connectedCallback() {
      super.connectedCallback();
      if (theme.config.motionReduced || typeof Motion === 'undefined') return;
      this.stage = this.querySelector('[data-panel-stage]');
      this.cols = Array.from(this.querySelectorAll('[data-panel]')).map((el) => ({
        el,
        photo: el.querySelector('[data-panel-photo]'),
        label: el.querySelector('[data-panel-label]'),
        body: el.querySelector('[data-panel-body]'),
        edge: el.querySelector('[data-panel-edge]'),
      }));
      this.finale = this.querySelector('[data-panel-finale]');
      if (!this.stage || this.cols.length < 2) return;
      belfortiWhileMatches(this, '(min-width: 768px)', () => this.arm());
    }

    arm() {
      const cols = this.cols;
      const n = cols.length;
      const steps = n + (this.finale ? 1 : 0);
      const wide = parseFloat(getComputedStyle(this).getPropertyValue('--bpn-wide')) / 100 || 0.64;
      const narrow = (1 - wide) / (n - 1);
      const finaleImg = this.finale ? this.finale.querySelector('img') : null;
      this.classList.add('is-armed');

      let width = this.stage.clientWidth;
      let shares = cols.map((_, i) => (i === 0 ? wide : narrow));
      let target = 0;
      let hover = -1;
      let fin = 0;
      let frame = null;

      const paint = () => {
        let x = 0;
        cols.forEach((c, i) => {
          const w = shares[i];
          const left = x;
          const right = x + w;
          const a = pnClamp((w - narrow) / (wide - narrow));
          const lx = (left * width).toFixed(1);
          c.el.style.clipPath = 'inset(0 ' + ((1 - right) * 100).toFixed(3) + '% 0 ' + (left * 100).toFixed(3) + '%)';
          c.photo.style.transform = 'translateX(' + (((left + right) / 2 - wide / 2) * width).toFixed(1) + 'px)';
          c.label.style.transform = 'translateX(' + lx + 'px)';
          c.label.style.opacity = (1 - a).toFixed(3);
          c.body.style.transform = 'translateX(' + lx + 'px) translateY(' + ((1 - a) * 1.5).toFixed(2) + 'rem)';
          c.body.style.opacity = a.toFixed(3);
          c.body.style.pointerEvents = a > 0.5 ? '' : 'none';
          if (c.edge) c.edge.style.transform = 'translateX(' + lx + 'px)';
          x = right;
        });
        if (this.finale) {
          this.finale.style.opacity = fin.toFixed(3);
          this.finale.style.pointerEvents = fin > 0.5 ? '' : 'none';
          if (finaleImg) finaleImg.style.transform = 'scale(' + (1.06 - 0.06 * fin).toFixed(4) + ')';
        }
      };
      const tick = () => {
        const open = hover >= 0 ? hover : target;
        let moving = false;
        shares = shares.map((s, i) => {
          const goal = i === open ? wide : narrow;
          const d = goal - s;
          if (Math.abs(d) < 0.0004) return goal;
          moving = true;
          return s + d * 0.11;
        });
        paint();
        frame = moving ? requestAnimationFrame(tick) : null;
      };
      const kick = () => { if (!frame) frame = requestAnimationFrame(tick); };

      const stop = Motion.scroll(({ y }) => {
        const p = y.progress;
        const k = Math.min(steps - 1, Math.floor(p * steps));
        if (k < n) {
          target = k;
          fin = 0;
        } else {
          target = n - 1;
          fin = pnEase(pnClamp((p * steps - k) / 0.5));
        }
        kick();
      }, { target: this, offset: ['start start', 'end end'] });

      // A fine pointer over the stage opens the panel under it; leaving hands back to the scroll.
      const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      const onMove = (e) => {
        if (!fine || fin > 0.5) return;
        const r = this.stage.getBoundingClientRect();
        const u = (e.clientX - r.left) / r.width;
        let x = 0;
        let h = n - 1;
        for (let i = 0; i < n; i++) { x += shares[i]; if (u < x) { h = i; break; } }
        if (h !== hover) { hover = h; kick(); }
      };
      const onLeave = () => { if (hover !== -1) { hover = -1; kick(); } };
      const onResize = () => { width = this.stage.clientWidth; paint(); };
      this.stage.addEventListener('pointermove', onMove);
      this.stage.addEventListener('pointerleave', onLeave);
      window.addEventListener('resize', onResize);
      paint();

      return () => {
        stop();
        cancelAnimationFrame(frame);
        frame = null;
        this.stage.removeEventListener('pointermove', onMove);
        this.stage.removeEventListener('pointerleave', onLeave);
        window.removeEventListener('resize', onResize);
        this.classList.remove('is-armed');
        cols.forEach((c) => [c.el, c.photo, c.label, c.body, c.edge].forEach((el) => el && el.removeAttribute('style')));
        if (this.finale) this.finale.removeAttribute('style');
        if (finaleImg) finaleImg.removeAttribute('style');
      };
    }
  }
  customElements.define('belforti-panels', BelfortiPanels);
}

// Walkthrough (sections/belforti-walkthrough.liquid): a camera over a screenshot. The scroll is
// cut into one equal stretch per step; the camera eases every frame (position and log zoom) toward
// the step the scroll has reached, translating and scaling the screenshot inside its frame so the
// step's spot sits at the centre (clamped at the edges). The marker (a ring around the step's box,
// or a pointer: dot, leader and tag on the exact spot) fades in once the camera settles, and the
// step lights up in the list. Clicking a step scrolls to its stretch.
// Phones (only when the section has a phone photo, upright): the frame sticks under the header
// and the steps pile up beneath it; the step whose top has passed a reading line just under the
// frame is the one the camera goes to, with the phone spots (data-mx/my) on the phone photo.
if (!customElements.get('belforti-walkthrough')) {
  class BelfortiWalkthrough extends BaseElement {
    connectedCallback() {
      super.connectedCallback();
      if (theme.config.motionReduced || typeof Motion === 'undefined') return;
      this.frame = this.querySelector('[data-walk-frame]');
      this.camera = this.querySelector('[data-walk-camera]');
      this.ring = this.querySelector('[data-walk-ring]');
      const num = (v, d) => (v === undefined || v === '' || Number.isNaN(Number(v)) ? d : Number(v));
      this.steps = Array.from(this.querySelectorAll('[data-walk-step]')).map((el) => ({
        el,
        btn: el.querySelector('[data-walk-go]'),
        x: num(el.dataset.x, 50) / 100,
        y: num(el.dataset.y, 50) / 100,
        mx: num(el.dataset.mx, num(el.dataset.x, 50)) / 100,
        my: num(el.dataset.my, num(el.dataset.y, 50)) / 100,
        z: Math.max(1, num(el.dataset.z, 100) / 100),
        bw: num(el.dataset.bw, 20) / 100,
        bh: num(el.dataset.bh, 12) / 100,
        marker: el.dataset.marker !== 'none',
        pin: null,
        tag: null,
        tw: 0,
      }));
      this.pins = Array.from(this.querySelectorAll('[data-walk-pin]'));
      this.pins.forEach((pin) => {
        const s = this.steps[Number(pin.dataset.walkPin)];
        if (s) {
          s.pin = pin;
          s.tag = pin.querySelector('[data-walk-tag]');
        }
      });
      if (!this.frame || !this.camera || this.steps.length < 2) return;
      belfortiWhileMatches(this, '(min-width: 768px)', () => this.arm(false));
      if (this.dataset.mobile === '1') belfortiWhileMatches(this, '(max-width: 767px)', () => this.arm(true));
    }

    arm(mobile) {
      const steps = this.steps;
      const n = steps.length;
      const armedClass = mobile ? 'is-armed-m' : 'is-armed';
      this.classList.add(armedClass);
      let W = this.frame.clientWidth;
      let H = this.frame.clientHeight;
      // The photo's box inside the frame: the frame itself, or, in a fixed frame, the photo
      // fitted inside it (letterboxed). Spots are fractions of the photo, so everything maps
      // through this box.
      const fit = this.dataset.fit === 'contain';
      const imgRatio = Number(mobile ? this.dataset.imgRatioM : this.dataset.imgRatio) || 0;
      let R = { ix: 0, iy: 0, iw: W, ih: H };
      const rect = () => {
        if (!fit || !imgRatio) { R = { ix: 0, iy: 0, iw: W, ih: H }; return; }
        if (imgRatio > W / H) { const ih = W / imgRatio; R = { ix: 0, iy: (H - ih) / 2, iw: W, ih }; }
        else { const iw = H * imgRatio; R = { ix: (W - iw) / 2, iy: 0, iw, ih: H }; }
      };
      rect();
      let target = 0;
      let current = -1;
      let frame = null;
      let ringOn = 0;
      const goalOf = (k) => (mobile
        ? { x: steps[k].mx, y: steps[k].my, lz: Math.log(steps[k].z) }
        : { x: steps[k].x, y: steps[k].y, lz: Math.log(steps[k].z) });
      let cam = goalOf(0);

      const paint = () => {
        const z = Math.exp(cam.lz);
        let tx = W / 2 - (R.ix + cam.x * R.iw) * z;
        let ty = H / 2 - (R.iy + cam.y * R.ih) * z;
        tx = Math.min(0, Math.max(W - W * z, tx));
        ty = Math.min(0, Math.max(H - H * z, ty));
        this.camera.style.transform = 'translate3d(' + tx.toFixed(1) + 'px, ' + ty.toFixed(1) + 'px, 0) scale(' + z.toFixed(4) + ')';
        const s = steps[target];
        const g = goalOf(target);
        if (this.pins.length) {
          // One pointer per step, pre-labelled: only the step's own moves and shows.
          this.pins.forEach((pin) => { if (pin !== s.pin) pin.style.opacity = '0'; });
          if (s.pin) {
            const px = (R.ix + g.x * R.iw) * z + tx;
            const py = (R.iy + g.y * R.ih) * z + ty;
            s.pin.style.transform = 'translate3d(' + px.toFixed(1) + 'px, ' + py.toFixed(1) + 'px, 0)';
            s.pin.style.opacity = ringOn.toFixed(3);
            s.pin.classList.toggle('is-below', py < 88);
            if (s.tag) {
              // Keep the tag inside the frame when the spot is near a side.
              const half = s.tw / 2;
              let dx = 0;
              if (px - half < 8) dx = 8 - (px - half);
              else if (px + half > W - 8) dx = W - 8 - (px + half);
              s.tag.style.transform = 'translateX(calc(-50% + ' + dx.toFixed(1) + 'px))';
            }
          }
        } else if (this.ring) {
          const rw = s.bw * R.iw * z;
          const rh = s.bh * R.ih * z;
          const rx = (R.ix + g.x * R.iw) * z + tx - rw / 2;
          const ry = (R.iy + g.y * R.ih) * z + ty - rh / 2;
          this.ring.style.width = rw.toFixed(1) + 'px';
          this.ring.style.height = rh.toFixed(1) + 'px';
          this.ring.style.transform = 'translate3d(' + rx.toFixed(1) + 'px, ' + ry.toFixed(1) + 'px, 0)';
          this.ring.style.opacity = s.marker ? ringOn.toFixed(3) : '0';
        }
      };
      const measure = () => steps.forEach((s) => { if (s.tag) s.tw = s.tag.offsetWidth; });
      const tick = () => {
        const g = goalOf(target);
        let moving = false;
        const ease = (a, b, k) => {
          const d = b - a;
          if (Math.abs(d) < 0.0005) return b;
          moving = true;
          return a + d * k;
        };
        cam = { x: ease(cam.x, g.x, 0.09), y: ease(cam.y, g.y, 0.09), lz: ease(cam.lz, g.lz, 0.09) };
        const away = Math.abs(cam.x - g.x) + Math.abs(cam.y - g.y) + Math.abs(cam.lz - g.lz);
        ringOn = ease(ringOn, away < 0.03 ? 1 : 0, 0.14);
        paint();
        frame = moving ? requestAnimationFrame(tick) : null;
      };
      const kick = () => { if (!frame) frame = requestAnimationFrame(tick); };
      const setTarget = (k) => {
        target = k;
        if (current !== k) {
          current = k;
          steps.forEach((s, i) => s.el.classList.toggle('is-on', i === k));
          ringOn = Math.min(ringOn, 0.2);
        }
        kick();
      };
      measure();
      setTarget(0);
      paint();

      // Phones: the reading line sits just under the stuck frame; the last step whose top has
      // crossed it is the one in hand. Desktop: the pinned scroll is cut into equal stretches.
      const lineOf = () => this.frame.getBoundingClientRect().bottom + 40;
      const pick = () => {
        const line = lineOf();
        let k = 0;
        for (let i = 0; i < n; i++) if (steps[i].el.getBoundingClientRect().top <= line) k = i;
        setTarget(k);
      };
      const stop = mobile
        ? Motion.scroll(pick, { target: this, offset: ['start end', 'end start'] })
        : Motion.scroll(({ y }) => setTarget(Math.min(n - 1, Math.floor(y.progress * n))), { target: this, offset: ['start start', 'end end'] });
      const jumps = steps.map((s, i) => {
        const go = () => {
          let top;
          if (mobile) {
            top = s.el.getBoundingClientRect().top + window.scrollY - lineOf() + 8;
          } else {
            const range = this.offsetHeight - window.innerHeight;
            top = this.getBoundingClientRect().top + window.scrollY + ((i + 0.5) / n) * range;
          }
          window.scrollTo({ top, behavior: theme.config.motionReduced ? 'auto' : 'smooth' });
        };
        if (s.btn) s.btn.addEventListener('click', go);
        return go;
      });
      // The frame's size settles after the header measures itself and images load, so watch it.
      const onResize = () => {
        W = this.frame.clientWidth;
        H = this.frame.clientHeight;
        rect();
        measure();
        paint();
      };
      const watcher = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(onResize);
      if (watcher) {
        watcher.observe(this.frame);
        steps.forEach((s) => { if (s.tag) watcher.observe(s.tag); }); // tags grow when the heading font lands
      }
      window.addEventListener('resize', onResize);

      return () => {
        stop();
        cancelAnimationFrame(frame);
        frame = null;
        if (watcher) watcher.disconnect();
        window.removeEventListener('resize', onResize);
        steps.forEach((s, i) => {
          if (s.btn) s.btn.removeEventListener('click', jumps[i]);
          s.el.classList.remove('is-on');
        });
        this.classList.remove(armedClass);
        this.camera.removeAttribute('style');
        if (this.ring) this.ring.removeAttribute('style');
        this.pins.forEach((pin) => { pin.removeAttribute('style'); pin.classList.remove('is-below'); });
        steps.forEach((s) => { if (s.tag) s.tag.removeAttribute('style'); });
      };
    }
  }
  customElements.define('belforti-walkthrough', BelfortiWalkthrough);
}

// Statement (sections/belforti-statement.liquid): a model's design in chapters. From 768px the
// stage pins and the scroll is cut into one equal stretch per chapter; the chapter the scroll has
// reached gets `is-on` on its copy, its photo and its rail item, and the CSS crossfades and settles
// them (no per-frame work). Clicking a rail item scrolls to its stretch. Phones, no-JS and reduced
// motion keep the chapters stacked.
if (!customElements.get('belforti-statement')) {
  class BelfortiStatement extends BaseElement {
    connectedCallback() {
      super.connectedCallback();
      if (theme.config.motionReduced || typeof Motion === 'undefined') return;
      this.chapters = Array.from(this.querySelectorAll('[data-stm-chapter]'));
      this.shots = Array.from(this.querySelectorAll('[data-stm-shot]'));
      this.rail = Array.from(this.querySelectorAll('[data-stm-rail] li'));
      if (this.chapters.length < 2) return;
      belfortiWhileMatches(this, '(min-width: 768px)', () => this.arm());
    }

    arm() {
      const n = this.chapters.length;
      let current = -1;
      this.classList.add('is-armed');
      const setActive = (k) => {
        if (k === current) return;
        current = k;
        this.chapters.forEach((el, i) => el.classList.toggle('is-on', i === k));
        this.shots.forEach((el, i) => el.classList.toggle('is-on', i === k));
        this.rail.forEach((el, i) => el.classList.toggle('is-on', i === k));
      };
      setActive(0);
      const stop = Motion.scroll(({ y }) => setActive(Math.min(n - 1, Math.floor(y.progress * n))), { target: this, offset: ['start start', 'end end'] });
      const jumps = this.rail.map((li, i) => {
        const btn = li.querySelector('[data-stm-go]');
        const go = () => {
          const range = this.offsetHeight - window.innerHeight;
          const top = this.getBoundingClientRect().top + window.scrollY + ((i + 0.5) / n) * range;
          window.scrollTo({ top, behavior: theme.config.motionReduced ? 'auto' : 'smooth' });
        };
        if (btn) btn.addEventListener('click', go);
        return [btn, go];
      });
      return () => {
        stop();
        jumps.forEach(([btn, go]) => { if (btn) btn.removeEventListener('click', go); });
        [...this.chapters, ...this.shots, ...this.rail].forEach((el) => el.classList.remove('is-on'));
        this.classList.remove('is-armed');
      };
    }
  }
  customElements.define('belforti-statement', BelfortiStatement);
}
